import SVGConfigPrintTool from "components/_common/icons/tool/SVGConfigPrintTool";
import SVGReleaseManagementTool from "components/_common/icons/tool/SVGReleaseManagementTool";
import React, { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { historyActions } from "redux/_core/history";
import { IStore } from "redux/store";
import { rootRoutes } from "routes";

type OwnProps = {};
type ToolListProps = PropsFromRedux & OwnProps;
function ToolList(props: ToolListProps) {
	const handleReleaseClick = () => {
		const url =
			rootRoutes.children.app.children.tools.children.list.children.releases.url();
		props.goTo("#releases", url);
	};
	const handleLogsClick = () => {
		const url =
			rootRoutes.children.app.children.tools.children.list.children.logsPdf.url();
		props.goTo("#releases", url);
	};
	const handlePortsCargoListClick = () => {
		const url =
			rootRoutes.children.app.children.tools.children.list.children.portsCargoList.url();
		props.goTo("#portsCargoList", url);
	};
	const handleDocSyncCheckClick = () => {
		const url =
			rootRoutes.children.app.children.tools.children.list.children.docSyncCheck.url();
		props.goTo("#docSyncCheck", url);
	};
	const handleCnrPatchesClick = () => {
		const url =
			rootRoutes.children.app.children.tools.children.list.children.cnrPatches.url();
		props.goTo("#cnrPatches", url);
	};
	const handleFaqClick = () => {
		const url = rootRoutes.children.app.children.tools.children.list.children.faq.url();
		props.goTo("#faq", url);
	};
	const handleInstancesClick = () => {
		const url =
			rootRoutes.children.app.children.tools.children.list.children.instances.url();
		props.goTo("#instances", url);
	};

	const ToolCard = ({
		icon,
		title,
		description,
		onClick,
		delay = 0,
	}: {
		icon: React.ReactNode;
		title: string;
		description: string;
		onClick: () => void;
		delay?: number;
	}) => {
		const [isVisible, setIsVisible] = useState(false);

		useEffect(() => {
			const timer = setTimeout(() => setIsVisible(true), delay);
			return () => clearTimeout(timer);
		}, [delay]);

		return (
			<div
				className={`group bg-white rounded-2xl p-8 cursor-pointer transition-all duration-500 ease-in-out hover:shadow-lg hover:bg-gradient-to-br from-white to-gray-50 transform ${
					isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
				}`}
				onClick={onClick}
			>
				<div className="flex flex-col items-center text-center relative before:absolute before:inset-0 before:bg-blue-50/0 before:rounded-xl before:transition-colors before:duration-500 group-hover:before:bg-blue-50/10">
					<div className="relative mb-6 text-[#6B7280] transition-all duration-500 ease-in-out group-hover:text-blue-600 group-hover:rotate-[8deg]">
						{icon}
					</div>
					<h3 className="relative text-lg font-semibold text-gray-900 mb-3 transition-colors duration-500 group-hover:text-blue-600">
						{title}
					</h3>
					<p className="relative text-[15px] leading-relaxed text-gray-600 max-w-xs transition-all duration-500 group-hover:text-gray-700">
						{description}
					</p>
				</div>
			</div>
		);
	};

	return (
		<div className="min-h-[calc(100vh-98px)] bg-gradient-to-br from-gray-50 to-gray-100 m-0">
			<div className="p-6 md:p-8 lg:p-12 max-w-[1600px] mx-auto">
				<div className="mb-12">
					<h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
						Tools
					</h1>
					<p className="text-gray-600 text-lg">
						Access and manage your workspace tools
					</p>
				</div>

				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 xl:gap-8">
					<ToolCard
						icon={
							<div className="w-16 h-16 flex items-center justify-center">
								<SVGConfigPrintTool />
							</div>
						}
						title="Config/Print Manager"
						description="Manage configs and prints."
						onClick={() => props.goToConfigsAndPrints()}
						delay={100}
					/>

					<ToolCard
						icon={
							<div className="w-16 h-16 flex items-center justify-center">
								<span className="text-5xl text-[#6B7280] bp3-icon bp3-icon-id-number" />
							</div>
						}
						title="Certificate Manager"
						description="Manage and track training certificates."
						onClick={() => props.goToTrainingCertificate()}
						delay={200}
					/>

					<ToolCard
						icon={
							<div className="w-16 h-16 flex items-center justify-center">
								<span className="text-5xl text-[#6B7280] bp3-icon bp3-icon-history" />
							</div>
						}
						title="Audit Log"
						description="Proof of change across Wayship modules."
						onClick={() => props.goToAuditLog()}
						delay={300}
					/>

					<ToolCard
						icon={
							<div className="w-16 h-16 flex items-center justify-center">
								<span className="text-5xl text-[#6B7280] bp3-icon bp3-icon-people" />
							</div>
						}
						title="Groups"
						description="Organize vessels into groups for configs/prints."
						onClick={() =>
							props.goTo(
								"#groups",
								rootRoutes.children.app.children.tools.children.list.children.groups.url()
							)
						}
						delay={400}
					/>

					<ToolCard
						icon={
							<div className="w-16 h-16 flex items-center justify-center">
								<SVGReleaseManagementTool />
							</div>
						}
						title="Release Manager"
						description="Manage release versions and deployments."
						onClick={handleReleaseClick}
						delay={500}
					/>

					<ToolCard
						icon={
							<div className="w-16 h-16 flex items-center justify-center">
								<span className="text-5xl text-[#6B7280] bp3-icon bp3-icon-timeline-events" />
							</div>
						}
						title="CNR Activity Monitor"
						description="Audit all CNR changes across vessels."
						onClick={() =>
							props.goTo(
								"#cnractivity",
								rootRoutes.children.app.children.tools.children.list.children.cnractivity.url()
							)
						}
						delay={600}
					/>

					<ToolCard
						icon={
							<div className="w-16 h-16 flex items-center justify-center">
								<span className="text-5xl text-[#6B7280] bp3-icon bp3-icon-calendar" />
							</div>
						}
						title="IDL Event Manager"
						description="Track vessel events at the Date Line."
						onClick={() =>
							props.goTo(
								"#idlevents",
								rootRoutes.children.app.children.tools.children.list.children.idlEvents.url()
							)
						}
						delay={700}
					/>
					<ToolCard
						icon={
							<div className="w-16 h-16 flex items-center justify-center">
								<span className="text-5xl text-[#6B7280] bp3-icon bp3-icon-dashboard" />
							</div>
						}
						title="CNR Processing Monitor"
						description="Monitor Signal/IMOS CNR queues."
						onClick={() =>
							props.goTo(
								"#cnrprocessingstatus",
								rootRoutes.children.app.children.tools.children.list.children.cnrProcessingStatus.url()
							)
						}
						delay={725}
					/>
					<ToolCard
						icon={
							<div className="w-16 h-16 flex items-center justify-center">
								<SVGReleaseManagementTool />
							</div>
						}
						title="Logs PDF"
						description="Generate and download PDF log reports."
						onClick={handleLogsClick}
						delay={500}
					/>

					<ToolCard
						icon={
							<div className="w-16 h-16 flex items-center justify-center">
								<span className="text-5xl text-[#6B7280] bp3-icon bp3-icon-flag" />
							</div>
						}
						title="Feature Flags"
						description="Manage vessel feature flags and configurations."
						onClick={() =>
							props.goTo(
								"#featureflags",
								rootRoutes.children.app.children.tools.children.list.children.featureFlags.url()
							)
						}
						delay={750}
					/>
					<ToolCard
						icon={
							<div className="w-16 h-16 flex items-center justify-center">
								<span className="text-5xl text-[#6B7280] bp3-icon bp3-icon-map" />
							</div>
						}
						title="Ports & Cargo List"
						description="View ports and cargo types for a tenant."
						onClick={handlePortsCargoListClick}
						delay={775}
					/>
					<ToolCard
						icon={
							<div className="w-16 h-16 flex items-center justify-center">
								<span className="text-5xl text-[#6B7280] bp3-icon bp3-icon-comparison" />
							</div>
						}
						title="Doc Sync Check"
						description="Compare _rev between wayship and vessel DB."
						onClick={handleDocSyncCheckClick}
						delay={785}
					/>
					<ToolCard
						icon={
							<div className="w-16 h-16 flex items-center justify-center">
								<span className="text-5xl text-[#6B7280] bp3-icon bp3-icon-changes" />
							</div>
						}
						title="CNR Patches"
						description="Patch CNR data, create bunker reports, and delete old CNR docs."
						onClick={handleCnrPatchesClick}
						delay={790}
					/>
					<ToolCard
						icon={
							<div className="w-16 h-16 flex items-center justify-center">
								<span className="text-5xl text-[#6B7280] bp3-icon bp3-icon-help" />
							</div>
						}
						title="FAQ Manager"
						description='Edit Frequently Asked Questions.'
						onClick={handleFaqClick}
						delay={800}
					/>
					{props.canAccessVesselInstances && (
						<ToolCard
							icon={
								<div className="w-16 h-16 flex items-center justify-center">
									<span className="text-5xl text-[#6B7280] bp3-icon bp3-icon-cloud-upload" />
								</div>
							}
							title="Vessel Instances"
							description="Deploy and manage dedicated per-vessel instances."
							onClick={handleInstancesClick}
							delay={815}
						/>
					)}
				</div>
			</div>
		</div>
	);
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
	// TEMPORARY: Vessel Instances tool is restricted to a single email.
	const userEmail = (store.app.auth.user as any)?.email as string | undefined;
	return {
		canAccessVesselInstances:
			userEmail?.toLowerCase() === "abdul.ghani@volteo.com",
	};
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {
		goToConfigsAndPrints() {
			dispatch(
				historyActions.push(
					"#configsAndPrints",
					rootRoutes.children.app.children.tools.children.list.children.configPrint.url()
				)
			);
		},
		goToAuditLog() {
			dispatch(
				historyActions.push(
					"#auditLog",
					rootRoutes.children.app.children.tools.children.list.children.auditLog.url()
				)
			);
		},
		goToTrainingCertificate() {
			dispatch(
				historyActions.push(
					"#trainingCertificate",
					rootRoutes.children.app.children.tools.children.list.children.trainingCertificate.url()
				)
			);
		},
		goTo(reference: string, url: string) {
			dispatch(historyActions.push(reference, url));
		},
	};
}

const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(ToolList);
